import { useEffect, useState } from 'react';
import {
  Sparkles,
  HelpCircle,
  BookOpen,
  Compass,
  ShieldCheck,
} from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import { getPublicCopilotConfig } from '../../services/publicCopilotApi';

const pillars = [
  {
    icon: Sparkles,
    title: 'Free, instant answers about CrawlerVision',
    description:
      'Ask anything about features, pricing, scoring, AI Overviews, or how the Analyzer works. Pulse answers in seconds — no signup, no credit card.',
    accent: 'bg-cv-navy',
  },
  {
    icon: BookOpen,
    title: 'Trained on the full public knowledge base',
    description:
      'Pulse pulls answers from the CrawlerVision FAQ, Knowledge Base, Learning Center, and the public AI manifests — so you always get the canonical, up-to-date explanation.',
    accent: 'bg-cv-teal',
  },
  {
    icon: Compass,
    title: 'Knows where every page is',
    description:
      'Ask "where is the partner program?" or "open the FAQ for AI Overviews" and Pulse points you straight to the right page — one click.',
    accent: 'bg-cv-gold',
  },
  {
    icon: HelpCircle,
    title: 'Escalates report questions to the deep agent',
    description:
      'Pasted a CrawlerVision report URL? Pulse hands the question to the full LLM and gives you an in-depth, report-specific answer (consumes credits, fully transparent).',
    accent: 'bg-cv-orange',
  },
];

const PublicAssistantHighlight = () => {
  const [visible, setVisible] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getPublicCopilotConfig();
        if (cancelled) return;
        setEnabled(!!cfg?.enabled);
        setVisible(!!cfg?.enabled && !!cfg?.homepage_usp_enabled);
      } catch {
        if (!cancelled) setVisible(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible || !enabled) return null;

  return (
    <section
      id="public-assistant"
      className="py-16 md:py-20"
      aria-label="Pulse — your free CrawlerVision assistant"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <div className="relative overflow-hidden rounded-3xl border border-cv-sky/70 bg-cv-sky/10 p-6 sm:p-10 shadow-md">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cv-sky/60 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-cv-sky/50 blur-3xl"
            />

            <div className="absolute top-4 right-4 sm:top-5 sm:right-5 z-10" aria-label="Free for everyone">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white shadow-lg bg-cv-teal ring-1 ring-white/40">
                <Sparkles className="w-3.5 h-3.5 drop-shadow-sm" />
                Free · No signup
              </span>
            </div>

            <div className="relative">
              <div className="max-w-3xl mb-8">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 border border-cv-sky/50 text-cv-navy text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
                  <Sparkles className="w-3.5 h-3.5" />
                  Meet Pulse
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-cv-navy leading-tight">
                  Get instant answers about{' '}
                  <span className="text-cv-navy">
                    CrawlerVision &mdash; before you sign up
                  </span>
                </h2>
                <p className="text-cv-navy/80 mt-3 text-base sm:text-lg leading-relaxed">
                  Pulse is the free visitor assistant baked into every public page. Ask "what is the
                  CV Score?", "how do AI Overviews work?", "where is your refund policy?" &mdash;
                  Pulse answers from the official docs and points you to the right page in one click.
                  Need a deep dive into your own report? Pulse can escalate to the full LLM on demand.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {pillars.map((p) => (
                  <div
                    key={p.title}
                    className="rounded-2xl border border-white/80 bg-white/90 backdrop-blur-sm p-5 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`shrink-0 w-11 h-11 rounded-xl ${p.accent} flex items-center justify-center cv-emboss`}>
                        <p.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-cv-navy mb-1">{p.title}</h3>
                        <p className="text-sm text-cv-navy/70 leading-relaxed">{p.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-6 text-xs text-cv-navy/60 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                Scope-guarded to public CrawlerVision content. Free tier is rate-limited; deep
                report analysis consumes credits and is fully transparent in the panel.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PublicAssistantHighlight;
